"use client";

import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { chartPointSeriesSummary } from "./record-dashboard-chart-summary";
import {
  compactNumber,
  isCurrencyDisplayKind,
  type ChartPoint,
  type MetricDisplayKind,
} from "./record-dashboard-model";
import {
  DASHBOARD_CHART_FRAME,
  DASHBOARD_LINE_COLOR,
  DashboardChart,
  DashboardTooltip,
} from "./record-dashboard-shell";

// Currency ticks carry the unit once in the widget header, not per tick.
export function axisTickKind(displayKind: MetricDisplayKind): MetricDisplayKind {
  return isCurrencyDisplayKind(displayKind) ? "number" : displayKind;
}

export function positiveTopDomain(
  headroom = 1.1,
): [number, (dataMax: number) => number] {
  return [
    0,
    (dataMax: number) => (
      Number.isFinite(dataMax) && dataMax > 0 ? dataMax * headroom : 1
    ),
  ];
}

// Axis-less mini trend for metric tiles; the tooltip names the bucket.
export function MetricSparkline({
  points,
  displayKind,
}: {
  points: ChartPoint[];
  displayKind: MetricDisplayKind;
}) {
  const gradientId = `metric-sparkline-${useId().replaceAll(":", "")}`;
  return (
    <div className="mlx-dashboard-chart h-9 w-full" aria-hidden>
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 220, height: 36 }}>
        <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <Tooltip
            cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.28, strokeDasharray: "3 3" }}
            content={<DashboardTooltip displayKind={displayKind} />}
          />
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={DASHBOARD_LINE_COLOR} stopOpacity={0.22} />
              <stop offset="90%" stopColor={DASHBOARD_LINE_COLOR} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            name="Value"
            stroke={DASHBOARD_LINE_COLOR}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 3, fill: DASHBOARD_LINE_COLOR, stroke: "hsl(var(--surface))", strokeWidth: 1.5 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DashboardSeriesChart({
  title,
  data,
  displayKind,
  seriesName,
  integerTicks = false,
}: {
  title: string;
  data: ChartPoint[];
  displayKind: MetricDisplayKind;
  seriesName: string;
  /** Count axes never show fractional ticks. */
  integerTicks?: boolean;
}) {
  const gradientId = `dashboard-series-${useId().replaceAll(":", "")}`;
  return (
    <DashboardChart
      label={title}
      summary={chartPointSeriesSummary(title, data, displayKind)}
    >
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 480, height: 220 }}>
        <AreaChart
          accessibilityLayer
          data={data}
          margin={DASHBOARD_CHART_FRAME.margin}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={DASHBOARD_LINE_COLOR} stopOpacity={0.24} />
              <stop offset="82%" stopColor={DASHBOARD_LINE_COLOR} stopOpacity={0.025} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.58} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={9}
            minTickGap={18}
          />
          <YAxis
            domain={positiveTopDomain(1.12)}
            allowDecimals={!integerTicks}
            tickLine={false}
            axisLine={false}
            tickMargin={7}
            width={DASHBOARD_CHART_FRAME.yAxisWidth}
            tickFormatter={(value) => compactNumber(Number(value), axisTickKind(displayKind))}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.28, strokeDasharray: "3 3" }}
            content={<DashboardTooltip displayKind={displayKind} />}
          />
          {data.length === 1 ? (
            <ReferenceLine
              y={data[0]!.value}
              stroke={DASHBOARD_LINE_COLOR}
              strokeDasharray="3 3"
              strokeOpacity={0.45}
            />
          ) : null}
          <Area
            type="monotone"
            dataKey="value"
            name={seriesName}
            stroke={DASHBOARD_LINE_COLOR}
            strokeWidth={2.25}
            fill={`url(#${gradientId})`}
            dot={data.length <= 12 ? {
              r: 2.25,
              fill: "hsl(var(--surface))",
              stroke: DASHBOARD_LINE_COLOR,
              strokeWidth: 1.5,
            } : false}
            activeDot={{
              r: 4,
              fill: DASHBOARD_LINE_COLOR,
              stroke: "hsl(var(--surface))",
              strokeWidth: 2,
            }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </DashboardChart>
  );
}
